import {
    createAction,
    createEmptyAction,
    createMapAction,
    immediateAction,
    immediateEmptyAction,
    immediateMapAction,
    RoomInfo,
} from "chalet-shared";
import * as I from "immutable";

import { FailedMessage } from "../reducers/persist";
import { EditModeInfo, RoomType, RoomView, RoomViews, ScrollPosition } from "../reducers/rooms";
import { Data, PageTextExtendedRecord, RoomGroup } from "../service/Data";
import { uuid } from "../utils/short-uuid";

type LoadHistoryHow = "around" | "lt" | "gt";

export type UndoResetInteractions = {
    roomViews: RoomView[];
    interactions: { room_id: string; ts: number }[];
};

export const actionCreators = {
    restoreRooms: createMapAction(
        "RESTORE_ROOMS",
        (x: { activeRooms: I.List<RoomView>; roomViews: RoomViews }) => x
    ),
    activateOrOpenRoomView: createMapAction(
        "ACTIVATE_OR_OPEN_ROOM_VIEW",
        (x: { roomId: string; type: RoomType }) => x
    ),
    setRoomViewAsActive: immediateMapAction(
        "SET_ROOM_VIEW_AS_ACTIVE",
        (x: { roomView: RoomView; focus: boolean }) => x
    ),
    setRoomViewAsSleeping: createAction("SET_ROOM_VIEW_AS_SLEEPING", (roomView?: RoomView) => ({
        type: "SET_ROOM_VIEW_AS_SLEEPING",
        roomView,
    })),
    onRoomViewFocus: createMapAction("ON_ROOM_VIEW_FOCUS", (x: RoomView) => x),
    onRoomViewBlur: createMapAction("ON_ROOM_VIEW_BLUR", (x: RoomView) => x),
    resetRoomViewToFocus: immediateEmptyAction("RESET_ROOM_VIEW_TO_FOCUS"),
    setRoomViewEditMode: createMapAction(
        "SET_ROOM_VIEW_EDIT_MODE",
        (x: { roomView: RoomView; message: PageTextExtendedRecord }) => x
    ),
    updateRoomViewEditMode: createMapAction(
        "UPDATE_ROOM_VIEW_EDIT_MODE",
        (x: { roomView: RoomView; editMode: EditModeInfo | undefined }) => x
    ),
    unsetRoomViewEditMode: createMapAction(
        "UNSET_ROOM_VIEW_EDIT_MODE",
        (x: { roomView: RoomView }) => x
    ),

    roomSearchRequested: createMapAction(
        "ROOM_SEARCH_REQUESTED",
        (x: { roomView: RoomView; searchQuery: string; cursor?: number }) => x
    ),
    roomSearchSucceeded: createMapAction(
        "ROOM_SEARCH_SUCCEEDED",
        (x: { roomView: RoomView; data: Data.SearchIn }) => x
    ),
    roomSearchFailed: createMapAction(
        "ROOM_SEARCH_FAILED",
        (x: { roomView: RoomView; err: any }) => x
    ),

    setFitRooms: immediateAction("SET_FIT_ROOMS", (cols: number, rows: number) => ({
        type: "SET_FIT_ROOMS",
        cols,
        rows,
        renderImmediate: true,
    })),

    setRosterHoveredRoomId: createMapAction(
        "SET_ROSTER_HOVERED_ROOM_ID",
        (x: string | undefined) => x
    ),

    loadRoomHistoryRequested: createAction(
        "LOAD_ROOM_HISTORY_REQUESTED",
        (payload: { roomView: RoomView; how: LoadHistoryHow; ts: number }) => ({
            type: "LOAD_ROOM_HISTORY_REQUESTED",
            payload,
        })
    ),
    loadRoomHistory: createAction(
        "LOAD_ROOM_HISTORY",
        (payload: { roomView: RoomView; how: LoadHistoryHow; data: Data.JoinIn }) => ({
            type: "LOAD_ROOM_HISTORY",
            payload,
        })
    ),

    loadJoinedRoomHistoryRequested: createAction(
        "LOAD_JOINED_ROOM_HISTORY_REQUESTED",
        (payload: { roomView: RoomView; how: LoadHistoryHow; ts: number }) => ({
            type: "LOAD_JOINED_ROOM_HISTORY_REQUESTED",
            payload,
        })
    ),
    loadJoinedRoomHistory: createAction(
        "LOAD_JOINED_ROOM_HISTORY",
        (payload: { roomView: RoomView; how: LoadHistoryHow; data: Data.JoinIn }) => ({
            type: "LOAD_JOINED_ROOM_HISTORY",
            payload,
        })
    ),

    sendRoomMessage: createAction(
        "ROOMS_SEND_MESSAGE",
        (
            roomView: RoomView,
            pendingId: string,
            raw: string,
            monolog: RoomInfo | undefined,
            uploads: Data.UploadData[]
        ) => ({
            type: "ROOMS_SEND_MESSAGE",
            roomView,
            pendingId,
            raw,
            monolog,
            uploads,
            ts: Date.now() * 1000,
        })
    ),

    sendRoomMessageFailed: createAction(
        "ROOMS_SEND_MESSAGE_FAILED",
        (roomView: RoomView, pendingId: string) => ({
            type: "ROOMS_SEND_MESSAGE_FAILED",
            roomView,
            pendingId,
        })
    ),

    discardFailedRoomMessage: createAction(
        "DISCARD_FAILED_ROOM_MESSAGE",
        (roomView: RoomView, pendingId: string) => ({
            type: "DISCARD_FAILED_ROOM_MESSAGE",
            roomView,
            pendingId,
        })
    ),

    updateRoomMessage: createAction(
        "ROOMS_UPDATE_MESSAGE",
        (roomView: RoomView, messageId: string, raw: string) => ({
            type: "ROOMS_UPDATE_MESSAGE",
            roomView,
            messageId,
            raw,
        })
    ),

    deleteRoomMessage: createAction(
        "ROOMS_DELETE_MESSAGE",
        (roomView: RoomView, messageId: string) => ({
            type: "ROOMS_DELETE_MESSAGE",
            roomView,
            messageId,
        })
    ),

    activateOrOpenRoom: createMapAction("ACTIVATE_OR_OPEN_ROOM", (x: { roomId: string }) => x),
    activateOrOpenRoomByUserId: createMapAction(
        "ACTIVATE_OR_OPEN_ROOM_BY_USER_ID",
        (x: { userId: string }) => x
    ),
    setRoomToOpen: createMapAction(
        "SET_ROOM_TO_OPEN",
        ({
            roomId,
            type,
            historyAroundTs,
            historyHighlighedMessage,
            searchQuery,
        }: {
            roomId: string;
            type: RoomType;
            historyAroundTs?: number;
            historyHighlighedMessage?: string;
            searchQuery?: string;
        }) =>
            RoomView({
                roomId,
                viewId: uuid(),
                type,
                historyAroundTs,
                historyHighlighedMessage,
                searchQuery,
            })
    ),
    openRoomRequested: createMapAction("OPEN_ROOM_REQUESTED", (x: { roomView: RoomView }) => x),
    openRoomSucceeded: immediateMapAction("OPEN_ROOM_SUCCEEDED", (x: { roomView: RoomView }) => x),
    closeRoomRequested: createMapAction("CLOSE_ROOM_REQUESTED", (x: RoomView[]) => x),
    closeRoomSucceeded: immediateMapAction("CLOSE_ROOM_SUCCEEDED", (x: RoomView[]) => x),
    roomSearchQueryUpdated: createMapAction(
        "ROOM_SEARCH_QUERY_UPDATED",
        (x: { roomView: RoomView; searchQuery?: string }) => x
    ),

    canJumpToBottomUpdated: createMapAction(
        "CAN_JUMP_TO_BOTTOM_UPDATED",
        (x: { roomView: RoomView; value: boolean }) => x
    ),
    canJumpToUnreadUpdated: createMapAction(
        "CAN_JUMP_TO_UNREAD_UPDATED",
        (x: { roomView: RoomView; value: boolean }) => x
    ),
    roomScrollPositionUpdated: createMapAction(
        "ROOM_SCROLL_POSITION_UPDATED",
        (x: { roomView: RoomView; value: ScrollPosition; messageId?: string }) => x
    ),

    checkIfTypingStuck: createMapAction("CHECK_IF_TYPING_STUCK", (x: { roomId: string }) => x),

    uploadFiles: createMapAction(
        "UPLOAD_FILES",
        ({ roomView, files }: { roomView: RoomView; files: Blob[] }) => {
            return {
                roomView,
                uploads: I.Map(files.map((file): [string, Blob] => [uuid(), file])),
            };
        }
    ),
    uploadFile: createMapAction(
        "UPLOAD_FILE",
        (
            x: { roomView: RoomView; tag: string } & (
                | { status: "cancel" }
                | { status: "progress"; progress: number }
                | { status: "done"; response: Data.UploadData }
                | { status: "error"; err: any })
        ) => x
    ),

    onJoinedRoom: createMapAction(
        "ON_JOINED_ROOM",
        (x: { roomId: string; data: Data.JoinIn; failedMessages: FailedMessage[] }) => x
    ),
    onLeftRoom: createMapAction("ON_LEFT_ROOM", (x: { roomId: string; data: Data.LeaveIn }) => x),

    onJoinError: createMapAction("ON_JOIN_ERROR", (x: { roomId: string; error: any }) => x),
    onDeleteTooLateError: createMapAction(
        "ON_DELETE_TOO_LATE_ERROR",
        (x: { roomId: string; expiration?: number }) => x
    ),

    removeRoomFromRoster: createAction(
        "REMOVE_ROOM_FROM_ROSTER",
        (roomView: RoomView, forumId?: string) => ({
            type: "REMOVE_ROOM_FROM_ROSTER",
            roomView,
            forumId,
        })
    ),
    removeRoomFromRosterError: createMapAction(
        "REMOVE_ROOM_FROM_ROSTER_ERROR",
        (x: { roomId: string }) => x
    ),
    showUndoResetInteractionsToaster: createMapAction(
        "SHOW_UNDO_RESET_INTERACTIONS_TOASTER",
        (x: UndoResetInteractions) => x
    ),
    undoResetInteractions: createMapAction(
        "UNDO_RESET_INTERACTIONS",
        (x: UndoResetInteractions) => x
    ),
    undoResetInteractionsError: createMapAction(
        "UNDO_RESET_INTERACTIONS_ERROR",
        (x: { retryArgs: UndoResetInteractions }) => x
    ),
    markRoomUnreadFromHere: createMapAction(
        "MARK_ROOM_UNREAD_FROM_HERE",
        (x: { roomView: RoomView; ts: number }) => x
    ),
    updateRoomFirstUnread: createMapAction(
        "UPDATE_ROOM_FIRST_UNREAD",
        (x: { roomId: string }) => x
    ),
    groupMarkAsRead: createMapAction("GROUP_MARK_AS_READ", (x: RoomGroup) => x),
    groupMarkAsReadError: createMapAction("GROUP_MARK_AS_READ_ERROR", (x: RoomGroup) => x),
    groupHide: createMapAction("GROUP_HIDE", (x: RoomGroup) => x),
    groupHideError: createMapAction("GROUP_HIDE_ERROR", (x: RoomGroup) => x),
    groupResetInteractionTimestamp: createMapAction(
        "GROUP_RESET_INTERACTION_TIMESTAMP",
        (x: RoomGroup) => x
    ),
    groupResetInteractionTimestampError: createMapAction(
        "GROUP_RESET_INTERACTION_TIMESTAMP_ERROR",
        (x: RoomGroup) => x
    ),
    groupUnfollow: createEmptyAction("GROUP_UNFOLLOW"),
    groupUnfollowError: createEmptyAction("GROUP_UNFOLLOW_ERROR)"),

    roomMentionSelected: immediateMapAction(
        "ROOM_MENTION_SELECTED",
        (x: { roomView: RoomView; roomId: string }) => x
    ),
    roomMentionsSet: immediateMapAction(
        "ROOM_MENTIONS_SET",
        (x: { roomView: RoomView; isOpen: boolean; prefix: string | undefined }) => x
    ),
    privateRoomAddMembersInputSet: immediateMapAction(
        "PRIVATE_ROOM_ADD_MEMBERS_INPUT_SET",
        (x: { roomView: RoomView; prefix: string }) => x
    ),
};
